/* eslint-disable @typescript-eslint/no-explicit-any */
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import type { GENDER_TYPES } from '../_features/motoboys/constants';
import type {
  Motoboy,
  PlaceBlockedTemp,
} from '../_features/motoboys/motoboy-list/types';
import type { FARE_OF_TYPE } from '@/features/fare/types';
import type { VEHICLE_TYPES } from '@/_features/motoboys/deliveryman-form/constants';
import type { ENUM_DELIVERYMAN_STATUS } from '@/_constants/enums';

export interface IDropdownOptions<V = any, T = string> {
  key: T;
  text: string;
  value: V;
}
interface Flag {
  key: string;
  value: boolean;
}
export interface IUser {
  celular: string;
  cpf: string;
  email: string;
  nome: string;
  token: string;
  managerLogType: ROLE_TYPES;
  permissions: Permissions;
  flags?: Flag[];
  municipioIds?: string[];
  _id: string;
}

export interface ILocation {
  latitude: number;
  longitude: number;
}

export interface ICity {
  _id: string;
  nome: string;
  uf: string;
  localizacao: ILocation;
}

export interface IAddress {
  logradouro: string;
  numero: string;
  bairro: string;
  municipioId: string;
  cep: string;
  complemento: string;
  referencia: string;
  municipioNomeUf?: string;
}

export interface IMotoboyCnh {
  categoria: string;
  emissao: string;
  numero: string;
  validade: string;
}

export interface IMotoboyBank {
  bancoAgencia: string;
  bancoAgenciaDigito: string;
  bancoCodigo: string;
  bancoNome: string;
  bancoNomeTransfeera: string;
  comprovanteUrl: string;
  cpfCnpjDestinatario: string;
  digitoConta: string;
  nomeDestinatario: string;
  numeroConta: string;
  tipoConta: string;
}

export interface IDocumentVehicle {
  anoFabricacao: string;
  anoModelo: string;
  chassi: string;
  cpf: string;
  exercicio: string;
  modelo: string;
  nome: string;
  placa: string;
  renavam: string;
}

export type StatusMotoboy =
  | 'BLOQUEADO'
  | 'OFFLINE'
  | 'DISPONIVEL'
  | 'EM_CORRIDA'
  | 'PRECADASTRO'
  | 'ENTREGA_ASSOCIADA'
  | 'REPROVADO'
  | 'EXCLUIDO';

interface LastStateActions {
  confirmedArrival?: boolean;
}

interface LastStateEstablishmentAddress {
  neighborhood: string;
  street: string;
  number: string;
  cityId: string;
  cityNameState: string;
}

export interface LastStateEstablishmentItem {
  establishmentId: string;
  establishmentName: string;
  establishmentImage: string;
  address: LastStateEstablishmentAddress;
  cellphone: string;
  originLocation: Location;
  position: number;
  isCollected: boolean;
}

export const USER_STATE = {
  TELA_INICIAL: 'TELA_INICIAL',
  DETALHES_DA_CORRIDA: 'DETALHES_DA_CORRIDA',
  ENTREGAS_DISPONIVEIS: 'ENTREGAS_DISPONIVEIS',
  SAIR_PARA_ENTREGAS: 'SAIR_PARA_ENTREGAS',
  SAIR_PARA_ENTREGA_DETALHE: 'SAIR_PARA_ENTREGA_DETALHE',
  RETORNO_AO_ESTABELECIMENTO: 'RETORNO_AO_ESTABELECIMENTO',
  AGUARDANDO_FINALIZACAO: 'AGUARDANDO_FINALIZACAO',
  FINALIZAR_ENTREGA: 'FINALIZAR_ENTREGA',
  PERSONALIZAR_CORRIDA: 'PERSONALIZAR_CORRIDA',
  ESTABELECIMENTOS_PARA_RETORNAR: 'ESTABELECIMENTOS_PARA_RETORNAR',
};

export interface ILastState {
  actions: LastStateActions;
  page: keyof typeof USER_STATE;
  value: any | null;
  currentPosition?: number;
  establishments?: LastStateEstablishmentItem[];
  status: ENUM_DELIVERYMAN_STATUS;
  pendingDeliveryCalledId: string;
}

interface IMotoboyBlockInfo {
  reasonToBlock: 'TEMPORARY' | 'PERMANENT';
  hasBlockedAll: boolean;
  places: PlaceBlockedTemp[];
  blockType: string;
  periodType?: string;
  period?: string;
  periodEstimated?: Date;
  blockedAt: string;
}

// TODO: Remove this interface and use the one from motoboy-list/types.ts
export interface IMotoboy {
  _id: string;
  createdAt: string;
  approvedAt: string;
  deletedAt?: string;
  nome: string;
  cpf: string;
  celular: string;
  oldCelular?: string;
  email: string;
  senha: string;
  municipioId: string;
  cityName: string;
  status: StatusMotoboy;
  fotoCnh: string | null;
  fotoPessoal: string | null;
  tipoVeiculo: VEHICLE_TYPES | null;
  fotoDocumentoVeiculo: string | null;
  documentoVeiculo: IDocumentVehicle;
  cnpj: string | null;
  dataNascimento: string | null;
  enderecoResidencia?: IAddress;
  codigo: string | null;
  qlBankAccountId: string;
  updatedAt: string;
  razaoSocial: string;
  nomeFantasia: string;
  cnh: IMotoboyCnh;
  contaBancaria: IMotoboyBank;
  lastLocalizacao: Localization;
  lastState: ILastState;
  isDedicated?: boolean;
  isBlocked: boolean;
  blockInfo?: IMotoboyBlockInfo;
  gender?: GENDER_TYPES;
  preRegistration?: Motoboy['preRegistration'];
  civilState?: Motoboy['civilState'];
  appVersion?: Motoboy['appVersion'];
  mobileOS?: Motoboy['mobileOS'];
  mobileOSVersion?: Motoboy['mobileOSVersion'];
  placesLinked?: Motoboy['placesLinked'];
  deliverymanCnhPictureKey?: Motoboy['deliverymanCnhPictureKey'];
  deliverymanCrlvPictureKey?: Motoboy['deliverymanCrlvPictureKey'];
  deliverymanFrontRgPictureKey?: Motoboy['deliverymanFrontRgPictureKey'];
  deliverymanBackRgPictureKey?: Motoboy['deliverymanBackRgPictureKey'];
  deliverymanDocumentSelfieKey?: Motoboy['deliverymanDocumentSelfieKey'];
  deliverymanWithRgPictureKey?: Motoboy['deliverymanWithRgPictureKey'];
  blocks?: Motoboy['blocks'];
  isQueroLogApproved?: Motoboy['isQueroLogApproved'];
}

export interface IEvent {
  _id: string;
  createdAt: string;
  updatedAt: string;
  evento: string;
  payload: any;
}

export type StatusDelivery =
  | 'PENDENTE'
  | 'ASSOCIADO'
  | 'ACEITO'
  | 'CHEGOU_NA_LOJA'
  | 'SAIU_DA_LOJA'
  | 'CHEGOU_NO_CLIENTE'
  | 'PERSONALIZAR_CORRIDA'
  | 'ENTREGUE'
  | 'FINALIZADO'
  | 'CANCELADO';
export interface IPlaceFare {
  createdAt?: string;
  valorEntregaBase: number;
  valorKmAdicional: number;
  taxaEntregaKmAdicional: number;
  taxaEntregaBase: number;
  qtdMaxEntregaGroup: number;
  entregaBaseMts: number;
  maxDistanceDropsToGroup: number;
  requiredReturnToConfirmPayMethods: PaymentMethodsTypes[];
  requiredFormaPagamentoToConfirmReturn?: string[];
  requiredNearPlaceToConfirm: boolean;
  minDistanceCloseTo?: number;
  raioMaxDistance: number;
  fareOfType: FARE_OF_TYPE;
  neighborhoods?: {
    _id?: string;
    name: string;
    location: {
      lat: number;
      lon: number;
    };
    logFee: number;
    deliveryValue: number;
  }[];
}

export interface IExtract {
  _id: string;
  tipo: 'DEBITO' | 'CREDITO';
  descricao: string;
  cents: number;
  balance: number;
  last_balance: string;
  custom_fields: {
    key: string;
    value: string;
  }[];
  created_at: string;
}

export interface Localization {
  type: string;
  coordinates: number[];
}

export interface Permissions {
  delivery: {
    all: boolean;
    associateDelivery: boolean;
    deliveryStatus: boolean;
    reopenDelivery: boolean;
    cancelDelivery: boolean;
    events: boolean;
  };
  deliveryman: {
    all: boolean;
    createDeliveryman: boolean;
    editDeliveryman: boolean;
    approvePreRegister: boolean;
    blockDeliveryman: boolean;
    dedicateDeliveryman: boolean;
  };
  place: {
    all: boolean;
    editPlace: boolean;
    credit: boolean;
    debit: boolean;
    createAndEditFlare: boolean;
    dedicateOrder: boolean;
  };
  city: {
    all: boolean;
    editFare: boolean;
  };
}

export interface IMember {
  _id: string;
  email: string;
  cpf: string;
  celular: string;
  isAtivo: boolean;
  nome: string;
  managerLogType: ROLE_TYPES;
  sigla?: TRoleChar;
  municipioIds?: {
    _id: string;
    nome: string;
  }[];
  createdAt: string;
  permissions?: Permissions;
}

export type TUserType = 'MOTOBOY' | 'PLACE';

export type TDeliveryStatus =
  | 'PENDENTE'
  | 'ASSOCIADO'
  | 'ACEITO'
  | 'CANCELADO'
  | 'CHEGOU_NA_LOJA'
  | 'SAIU_DA_LOJA'
  | 'CHEGOU_NO_CLIENTE'
  | 'PERSONALIZAR_CORRIDA'
  | 'ENTREGUE'
  | 'FINALIZADO';

export type TSortTypes = 'asc' | 'desc';

export type PaymentMethodsTypes = 'DEBITO' | 'CREDITO' | 'DINHEIRO' | 'ONLINE';

export type PaymentMethodsTypesThatNeedReturnTypes =
  | 'DEBITO'
  | 'CREDITO'
  | 'DINHEIRO';

export interface Franchise {
  _id: string;
  nome: string;
  codigoIbge: string;
  localizacao: ILocation;
  uf: string;
  raioMaxDistance: number;
  valorEntregaBase: number;
  taxaEntregaBase: number;
  valorKmAdicional: number;
  createdAt: string;
  updatedAt: string;

  entregaBaseMts: number;
  maxDistanceDropsToGroup: number;
  qtdMaxEntregaGroup: number;
  taxaEntregaKmAdicional: number;
  requiredReturnToConfirmPayMethods: PaymentMethodsTypes[];
  requiredFormaPagamentoToConfirmReturn: string[];
  requiredNearPlaceToConfirm: boolean;
  minDistanceCloseTo?: number;
  fareOfType?: FARE_OF_TYPE;
  isCloud: boolean;
  hasCloudOperationOnCity?: boolean;
}

export type FranchiseMini = Pick<
  Franchise,
  '_id' | 'nome' | 'uf' | 'localizacao'
>;

export interface Submenu {
  title: string;
  key: string;
  keyPermissao?: string;
  href: string;
  icon: IconDefinition;
  routeLabel?: string;
  permissions?: ROLE_TYPES[];
}

export interface Menu {
  title: string;
  desc?: string;
  key: string;
  href: string;
  icon: IconDefinition;
  routeLabel?: string;
  submenus?: Submenu[];
  permissions?: ROLE_TYPES[];
  stageOnly?: boolean;
}

type TRoleChar = 'A' | 'OL' | 'S';

export enum ROLE_TYPES {
  'ADMIN' = 'ADMIN',
  'LOGISTIC_OPERATOR' = 'LOGISTIC_OPERATOR',
  'SUPPORT' = 'SUPPORT',
}

export type TWeekDay =
  | 'segunda'
  | 'terca'
  | 'quarta'
  | 'quinta'
  | 'sexta'
  | 'sabado'
  | 'domingo';

export interface IWeekDay {
  name: string;
  shortName: string;
}

export interface IWeekData {
  horaInicial: string;
  horaFinal: string;
}

export type IMotoboyPlaceDedication = Record<TWeekDay | string, IWeekData[]>;

export type IMotoboyDedication = Record<string, IMotoboyPlaceDedication>;
export interface IMotoboyMinimalInfo {
  nome: string;
  _id: string;
}

export interface IPlaceMinimalInfo {
  nomeExibicao: string;
  _id: string;
}

export interface IPlaceMinimalFareInfo {
  _id: string;
  createdAt: string;
  placeFare?: IPlaceFare;
  nomeExibicao: string;
}
